// ChatTab.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  CircularProgress,
  styled,
  Tooltip,
} from '@mui/material';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { startChatThread, sendChat, endChatThread } from '../../services/api';

// Custom auto-resizing input.
const CustomInput = styled(TextareaAutosize)(({ theme }) => ({
  width: '100%',
  fontSize: '1rem',
  padding: '10px',
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  fontFamily: 'inherit',
  resize: 'vertical',
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

// Updated Custom card component (wider and without being flush to the container edges)
const CustomCard = styled(Box)(({ theme }) => ({
  width: '90%',       // Card takes up 90% of the container's width
  maxWidth: 1200,      // Maximum width increased from 800 to 1200px
  backgroundColor: '#fff',
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[3],
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

function ChatTab({ isSidebarOpen, chatState, setChatState }) {
  const theme = useTheme();
  const { messages, threadId } = chatState;
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isNewChatLoading, setIsNewChatLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const previousThreadIdRef = useRef(threadId);

  // Auto-scroll to the bottom when messages update.
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Track thread changes to properly handle analytics
  useEffect(() => {
    // If threadId changes and there was a previous thread, that thread is ending
    if (previousThreadIdRef.current && threadId !== previousThreadIdRef.current) {
      // End the previous thread for analytics purposes
      endChatThread(previousThreadIdRef.current).catch(console.error);
    }
    
    // Update the previous thread reference
    previousThreadIdRef.current = threadId;
  }, [threadId]);

  // Set a flag when component mounts to indicate we're on the chat tab
  useEffect(() => {
    sessionStorage.setItem('chatTabActive', 'true');
    const currentThreadId = threadId;
    
    // When component unmounts
    return () => {
      // Signal that we're leaving the chat tab
      sessionStorage.removeItem('chatTabActive');
      
      // Only end chat thread on true page navigation or refresh,
      // not on tab switching within the app
      window.addEventListener('beforeunload', () => {
        if (currentThreadId) {
          endChatThread(currentThreadId).catch(console.error);
        }
      }, { once: true });
    };
  }, [threadId]);

  // End chat thread if the user closes the browser.
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (threadId) {
        endChatThread(threadId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () =>
      window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [threadId]);

  // Function to handle sending a message.
  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    setIsLoading(true);
    const userMessage = input.trim();
    setInput('');

    // Add the user's message immediately.
    setChatState((prevState) => ({
      ...prevState,
      messages: [...prevState.messages, { sender: 'user', text: userMessage }],
    }));

    try {
      let currentThreadId = threadId;
      // If no thread exists, start a new one.
      if (!currentThreadId) {
        const { thread_id } = await startChatThread();
        currentThreadId = thread_id;
        setChatState((prevState) => ({ ...prevState, threadId: thread_id }));
      }
      // Send the user's message and get a response.
      const { response } = await sendChat(currentThreadId, userMessage);
      setChatState((prevState) => ({
        ...prevState,
        messages: [...prevState.messages, { sender: 'ai', text: response }],
      }));
    } catch (error) {
      console.error('Chat error:', error);
      setChatState((prevState) => ({
        ...prevState,
        messages: [
          ...prevState.messages,
          {
            sender: 'ai',
            text:
              'Sorry, there was an error processing your request. Please try again.',
          },
        ],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key events in the text area.
  const handleKeyDown = (e) => {
    // If Enter is pressed without Shift, send the message.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle starting a new chat
  const handleNewChat = async () => {
    if (threadId && !isNewChatLoading) {
      try {
        setIsNewChatLoading(true);
        // End the current thread
        await endChatThread(threadId);
        
        // Reset messages and threadId
        setChatState({
          messages: [],
          threadId: null
        });
      } catch (error) {
        console.error('Error ending chat thread:', error);
      } finally {
        setIsNewChatLoading(false);
      }
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        // Set the container height to fill the viewport minus the header and footer (170px here)
        height: 'calc(100vh - 170px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        overflow: 'hidden', // Prevents scrolling of the ChatTab page itself
      }}
    >
      {/* The Chat Card fills this container, but we subtract 16px so its bottom isn't flush */}
      <CustomCard sx={{ height: 'calc(100% - 16px)' }}>
        {/* Header Section */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.grey[100],
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
            >
              Grant Chat Assistant
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
            >
              Ask a question or type a message to begin. For example: "Give me grants
              relating to AI and Healthcare?"
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                mt: 1,
                fontStyle: 'italic',
              }}
            >
              Disclaimer: The responses provided by this assistant may not always be
              accurate. Please verify any critical information from official sources.
            </Typography>
          </Box>
          {messages.length > 0 && (
            <Tooltip title="Start a new chat">
              <Button
                onClick={handleNewChat}
                variant="outlined"
                startIcon={isNewChatLoading ? <CircularProgress size={20} /> : <AddCircleOutlineIcon />}
                disabled={isNewChatLoading}
                sx={{
                  height: 'fit-content',
                  whiteSpace: 'nowrap',
                  ml: 2,
                }}
              >
                {isNewChatLoading ? 'Starting New Chat...' : 'New Chat'}
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Chat Area */}
        {messages.length === 0 ? (
          // Welcome State (no messages yet)
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              p: 3,
              gap: 2,
              backgroundColor: '#fff',
            }}
          >
            <Typography variant="h5" sx={{ color: theme.palette.text.primary }}>
              Welcome!
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              You can ask me anything – whether it's for information, help with a task,
              or just a friendly conversation.
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 500 }}>
              <CustomInput
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message here..."
                minRows={2}
                maxRows={4}
                disabled={isLoading}
                maxLength={2048}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleSendMessage}
                disabled={!input.trim() || isLoading}
                sx={{
                  py: 1.5,
                  mt: 1,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  fontSize: '1rem',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    opacity: 0.6,
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: '#fff' }} />
                ) : (
                  'Send Message'
                )}
              </Button>
            </Box>
          </Box>
        ) : (
          // Chat History View (when messages exist)
          <>
            <Box
              sx={{
                flexGrow: 1,
                overflowY: 'auto', // Only the chat history scrolls within the card.
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                p: 2,
                backgroundColor: '#fff',
              }}
            >
              {messages.map((msg, index) => (
                <Paper
                  key={index}
                  sx={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word',
                    alignSelf: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                    backgroundColor:
                      msg.sender === 'user'
                        ? theme.palette.primary.light
                        : theme.palette.grey[200],
                    color:
                      msg.sender === 'user'
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                    p: 1.5,
                    maxWidth: '70%',
                    borderRadius: theme.spacing(1),
                    boxShadow: theme.shadows[1],
                  }}
                >
                  {msg.text}
                </Paper>
              ))}
              {isLoading && (
                <Box
                  sx={{
                    alignSelf: 'flex-start',
                    p: 1.5,
                    maxWidth: '70%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <CircularProgress size={24} sx={{ color: theme.palette.text.secondary }} />
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
            {/* Input Section */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                backgroundColor: '#fff',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <CustomInput
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter your message..."
                  minRows={1}
                  maxRows={4}
                  disabled={isLoading}
                  maxLength={2048}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!input.trim() || isLoading}
                  sx={{
                    minWidth: 120,
                    py: 1.5,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    fontSize: '1rem',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      opacity: 0.6,
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                  ) : (
                    'Send'
                  )}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CustomCard>
    </Box>
  );
}

export default ChatTab;
